import React, { useMemo, useState } from 'react';
import { Modal, Radio, Tag } from 'antd';
import { useHistory as useRouterHistory } from 'react-router-dom';
import ResultTable from '../../components/table/ResultTable';
import ResultHeader from '../../components/table/ResultHeader';
import ResultBody from '../../components/table/ResultBody';
import ResultRow from '../../components/table/ResultRow';
import ResultHeaderCell from '../../components/table/ResultHeaderCell';
import ResultBodyCell from '../../components/table/ResultBodyCell';
import HecRasHelper from '../../helpers/HecRasHelper';
import { AppActions, useAppContext } from '../../context/AppContext';
import { GetMessageCodeText } from '../outputs/MessageCodes';
import AppColor from '../../components/AppColor';

// Human-readable description per message code. Codes shared across categories
// (e.g. ST 01, ST 06, ST 10) have the same meaning so a flat map is enough.
const MESSAGE_DESCRIPTIONS = {
    // Cross Section
    'XS 05': 'Hydraulic thresholds exceeded; potential need for additional downstream cross sections.',
    'XS 06': 'Inconsistent left and right bank station elevations.',
    'FW 02': 'Encroachment station placement issues.',
    'FL 05': 'Flow rate mismatch between upstream and downstream bridge cross sections.',
    'OP 01': 'Profile elevation is not contained within the cross section limits.',
    'RT 01': 'Non-standard contraction/expansion coefficients at natural cross sections.',
    'RT 02': 'Non-standard contraction/expansion coefficients at structure cross sections.',
    'RT 05': "Missing horizontally varied Manning's n value at the bank station.",
    'XY 01': 'Missing cross section georeferencing.',
    'XY 02': 'Bank stations do not enclose the stream centerline.',
    'XY 14': 'Cutline misses stream centerline (centerline logically extended to calculate intersection).',
    'XY 03': 'Cutline length does not match the cross section length.',
    'XY 05': 'Excessive skew angle between the cutline and the stream centerline.',
    'XY 07': 'Cross section cutline does not intersect the stream centerline.',
    'XY 10': 'Cross section cutlines intersect with each other.',
    'XY 15': 'Discrepancy in defined vs. estimated Overbank downstream reach lengths.',
    'XY 11': 'Discrepancy in defined vs. calculated Channel downstream reach lengths.',
    'XY 19': 'Sharp turns detected in cross section cutline segments.',
    // Bridge
    'XY 06': 'Bridge skew angle exceeds threshold.',
    'ST 01': 'Contraction/expansion ratios deviate from user preferences.',
    'ST 02': 'Bridges do not follow recommended modeling methods.',
    'ST 03': 'Maximum submergence ratio differs from the default value (0.98).',
    'ST 05': 'Missing bridge section at specified river station.',
    'ST 06': 'Inconsistent ineffective flow areas at upstream and downstream cross sections.',
    'ST 12': 'Inconsistent upstream and downstream abutment slopes.',
    'ST 15': 'Inconsistent upstream and downstream bridge opening widths.',
    'ST 10': 'Bounding cross sections are outside the recommended distance from the structure face.',
    // Culvert
    'RT 06': 'Culvert entrance/exit loss coefficient outside recommended limits.',
    'RT 07': "Culvert Manning's n value outside recommended limits.",
    'RT 08': 'Weir flow coefficient outside recommended limits.',
    'ST 04': 'Maximum submergence ratio differs from the default value (0.98).',
    'ST 07': 'Culvert geometry does not fit between the bounding cross sections.',
    'ST 09': 'Culvert centerline station is not located within the channel.',
    'ST 13': 'Upstream invert elevation is lower than downstream invert (adverse slope).',
    'ST 14': 'Culvert slope exceeds the defined maximum threshold.',
    // Overall Flow and Geometry
    'FW 05': 'Flow rate mismatch between 100-year and floodway profiles.',
    'XY 16': 'Reach invert line is not drawn upstream to downstream.',
};

const getDescription = (code) => MESSAGE_DESCRIPTIONS[code] || '';

// Walk every operation child in a history and return the first child whose
// result list owns a message matching `messageCode` (e.g. 'XS 06'). Returned
// child is the same shape Run Results uses for selectedOperation.
const findOperationForMessageCode = (history, messageCode) => {
    if (!history?.operations || !messageCode) return null;
    for (const operation of history.operations) {
        for (const child of operation?.children || []) {
            const results = child?.data?.result || [];
            for (const r of results) {
                if (GetMessageCodeText(child.key, r?.error_id) === messageCode) {
                    return child;
                }
            }
        }
    }
    return null;
};

export default function MessageCountsModal({ visible, onClose, histories }) {
    const [selectedCategory, setSelectedCategory] = useState('crossSection'); // 'crossSection', 'bridge', 'culvert', 'overall'
    const [selectedDetailCode, setSelectedDetailCode] = useState(null);

    const { dispatchProject, resolveHistorySelection } = useAppContext();
    const routerHistory = useRouterHistory();

    const checkHistories = useMemo(
        () => (histories || []).filter(h => h && h.type === 'Check'),
        [histories]
    );

    const analysisData = useMemo(() => {
        if (checkHistories.length === 0) return null;

        const rows = HecRasHelper.aggregateMessageCountRows(checkHistories);

        return {
            crossSectionData: rows.crossSection,
            bridgeData: rows.bridge,
            culvertData: rows.culvert,
            overallData: rows.overall,
        };
    }, [checkHistories]);

    // Get current data based on selected category
    const currentData = useMemo(() => {
        if (!analysisData) return [];
        if (selectedCategory === 'bridge') return analysisData.bridgeData;
        if (selectedCategory === 'culvert') return analysisData.culvertData;
        if (selectedCategory === 'overall') return analysisData.overallData;
        return analysisData.crossSectionData;
    }, [analysisData, selectedCategory]);

    // For the currently expanded message code, compute the per-plan breakdown
    // — only plans where the code actually fired are shown, since the user
    // asked "hangi plan/projeden geliyor" (which plan/project this is from).
    const detailRows = useMemo(() => {
        if (!selectedDetailCode) return [];
        const rows = [];
        for (const history of checkHistories) {
            const planRows = HecRasHelper.buildMessageCountRows(history);
            const row = planRows[selectedCategory]?.find(r => r.messageCode === selectedDetailCode);
            if (!row || row.count <= 0) continue;
            const resolved = resolveHistorySelection(history) || { base: {} };
            rows.push({
                historyId: history.id,
                date: history.date,
                projectTitle: resolved?.base?.project?.information?.title?.trim() || '-',
                modelType: resolved?.base?.project?.modelType,
                planTitle: resolved?.base?.plan?.type || '-',
                river: history?.selection?.base?.river?.river || null,
                reach: history?.selection?.base?.river?.reach || null,
                count: row.count,
                total: row.total,
            });
        }
        return rows;
    }, [checkHistories, selectedDetailCode, selectedCategory, resolveHistorySelection]);

    const handleRowClick = (messageCode) => {
        setSelectedDetailCode(prev => prev === messageCode ? null : messageCode);
    };

    const handleDetailRowClick = (row) => {
        const history = checkHistories.find(h => h.id === row.historyId);
        const operation = findOperationForMessageCode(history, selectedDetailCode);
        if (operation) {
            dispatchProject({
                type: AppActions.SET_SELECTED_HISTORY_ID,
                data: { id: row.historyId, operation },
            });
        } else {
            dispatchProject({
                type: AppActions.SET_SELECTED_HISTORY_ID,
                data: { id: row.historyId },
            });
        }
        dispatchProject({
            type: AppActions.SET_SELECTED_ERROR_ID,
            data: { errorId: selectedDetailCode },
        });
        onClose();
        routerHistory.push('/app/results');
    };

    if (checkHistories.length === 0) {
        return (
            <Modal
                title="Message Counts"
                open={visible}
                onCancel={onClose}
                footer={null}
                width={900}
                bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto' }}
                styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflow: 'auto' } }}
            >
                <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
                    No history data available. Please run a check first.
                </div>
            </Modal>
        );
    }

    const categoryTotal = currentData?.[0]?.total || 0;

    return (
        <Modal
            title="Message Counts"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={900}
            bodyStyle={{ maxHeight: 'calc(100vh - 160px)', overflow: 'auto' }}
            styles={{ body: { maxHeight: 'calc(100vh - 160px)', overflow: 'auto' } }}
        >
            <div style={{ marginBottom: 12 }}>
                <Radio.Group
                    value={selectedCategory}
                    onChange={(e) => {
                        setSelectedCategory(e.target.value);
                        setSelectedDetailCode(null);
                    }}
                    buttonStyle="solid"
                >
                    <Radio.Button value="crossSection">Cross Section</Radio.Button>
                    <Radio.Button value="bridge">Bridge</Radio.Button>
                    <Radio.Button value="culvert">Culvert</Radio.Button>
                    <Radio.Button value="overall">Overall Flow and Geometry</Radio.Button>
                </Radio.Group>
            </div>

            {(() => {
                const cardStyle = {
                    flex: 1,
                    padding: '12px 16px',
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: 6,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                };
                const labelStyle = { fontSize: 12, color: '#8c8c8c', marginBottom: 4 };
                const valueStyle = { fontSize: 22, fontWeight: 600, lineHeight: 1.2, color: '#262626' };
                const categoryLabel = selectedCategory === 'bridge' ? 'Bridges'
                    : selectedCategory === 'culvert' ? 'Culverts'
                    : selectedCategory === 'overall' ? 'Overall'
                    : 'Cross Sections';
                const flagged = (currentData || []).filter(r => r.count > 0).length;
                return (
                    <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Plans</div>
                            <div style={valueStyle}>{checkHistories.length}</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Total {categoryLabel}</div>
                            <div style={valueStyle}>{categoryTotal}</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Message Codes</div>
                            <div style={valueStyle}>{currentData?.length || 0}</div>
                        </div>
                        <div style={cardStyle}>
                            <div style={labelStyle}>Codes With Hits</div>
                            <div style={valueStyle}>{flagged}</div>
                        </div>
                    </div>
                );
            })()}

            {(!currentData || currentData.length === 0) ? (
                <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No data available for this category</div>
            ) : (
                <ResultTable style={{ width: '100%' }} maxWidth={'100%'}>
                    <ResultHeader>
                        <ResultRow>
                            <ResultHeaderCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(15) }}>ID</ResultHeaderCell>
                            <ResultHeaderCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(65) }}>Description</ResultHeaderCell>
                            <ResultHeaderCell style={{ textAlign: 'right', width: HecRasHelper.getWidth(20) }}>Count</ResultHeaderCell>
                        </ResultRow>
                    </ResultHeader>
                    <ResultBody>
                        {currentData.map((row, index) => {
                            const isSelected = row.messageCode === selectedDetailCode;
                            return (
                                <ResultRow
                                    key={`${row.messageCode}-${index}`}
                                    onClick={() => handleRowClick(row.messageCode)}
                                    style={{
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#e6f4ff' : undefined,
                                    }}
                                >
                                    <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(15), fontWeight: isSelected ? 600 : undefined }}>{row.messageCode}</ResultBodyCell>
                                    <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(65) }}>{getDescription(row.messageCode)}</ResultBodyCell>
                                    <ResultBodyCell style={{ textAlign: 'right', width: HecRasHelper.getWidth(20) }}>{row.count} / {row.total}</ResultBodyCell>
                                </ResultRow>
                            );
                        })}
                    </ResultBody>
                </ResultTable>
            )}

            {selectedDetailCode && (
                <div style={{ marginTop: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <b>Plans contributing to {selectedDetailCode}</b>
                        <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                            (click a row to open its result)
                        </span>
                    </div>
                    {detailRows.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 16, color: '#999', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                            No plan in the current run produced this message.
                        </div>
                    ) : (
                        <ResultTable style={{ width: '100%' }} maxWidth={'100%'}>
                            <ResultHeader>
                                <ResultRow>
                                    <ResultHeaderCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(40) }}>Project</ResultHeaderCell>
                                    <ResultHeaderCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(20) }}>Plan</ResultHeaderCell>
                                    <ResultHeaderCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(25) }}>River - Reach</ResultHeaderCell>
                                    <ResultHeaderCell style={{ textAlign: 'right', width: HecRasHelper.getWidth(15) }}>Count</ResultHeaderCell>
                                </ResultRow>
                            </ResultHeader>
                            <ResultBody>
                                {detailRows.map((row, index) => (
                                    <ResultRow
                                        key={`${row.historyId}-${index}`}
                                        onClick={() => handleDetailRowClick(row)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(40) }}>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                {row.modelType && <Tag style={{ marginRight: 0 }}>{row.modelType}</Tag>}
                                                <a style={{ color: AppColor.mainColor }}>{row.projectTitle}</a>
                                            </span>
                                        </ResultBodyCell>
                                        <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(20) }}>{row.planTitle}</ResultBodyCell>
                                        <ResultBodyCell style={{ textAlign: 'left', width: HecRasHelper.getWidth(25) }}>
                                            {row.river ? `${row.river} - ${row.reach}` : '-'}
                                        </ResultBodyCell>
                                        <ResultBodyCell style={{ textAlign: 'right', width: HecRasHelper.getWidth(15) }}>{row.count} / {row.total}</ResultBodyCell>
                                    </ResultRow>
                                ))}
                            </ResultBody>
                        </ResultTable>
                    )}
                </div>
            )}
        </Modal>
    );
}
